import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Table, Card, Button, Input, Select, Space, Tag, Modal, Form,
  Upload, message, Row, Col, Tooltip, Popconfirm, Descriptions,
  Tabs, Typography, InputNumber, DatePicker, Divider,
} from 'antd';
import {
  SearchOutlined, UploadOutlined, DownloadOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined, CalendarOutlined,
  BarChartOutlined, ReloadOutlined, MinusCircleOutlined,
  EnvironmentOutlined, FilterOutlined, CloseCircleOutlined,
  FileExcelOutlined, AppstoreOutlined, CheckCircleOutlined,
  HistoryOutlined, ExperimentOutlined, AuditOutlined,
  FileDoneOutlined, UserOutlined, TeamOutlined, CarOutlined,
  QrcodeOutlined, PrinterOutlined, QuestionCircleOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { scheduleAPI, businessTripAPI, masterAPI, qrAPI } from '../services/api';
import { getUsername } from '../services/authToken';
import { brand, gray, state, modalWidth } from '../theme/tokens';
import { SummerIcon, WinterIcon, SeasonBadge } from '../components/SeasonIcon';
import { EmptyState } from '../components/common';

const DATE_FORMAT = 'YYYY-MM-DD';
/** 문자열 → dayjs (파싱 가능하면 변환, 아니면 null) */
const toDay = (v: string | null | undefined) => {
  if (!v) return null;
  const d = dayjs(v, DATE_FORMAT, true);
  return d.isValid() ? d : null;
};
/** dayjs → 문자열 */
const fromDay = (d: any) => (d && dayjs.isDayjs(d) ? d.format(DATE_FORMAT) : (d || ''));

const { Option } = Select;
const { RangePicker } = DatePicker;

const TRIP_WORK_TYPES = [
  { value: '외근', label: '외근', icon: '🚗', group: 'out' },
  { value: '오전외근', label: '오전외근', icon: '🚗', group: 'out' },
  { value: '오후외근', label: '오후외근', icon: '🚗', group: 'out' },
  { value: '내근', label: '내근', icon: '🏢', group: 'in' },
  { value: '오전내근', label: '오전내근', icon: '🏢', group: 'in' },
  { value: '오후내근', label: '오후내근', icon: '🏢', group: 'in' },
  { value: '연차', label: '연차', icon: '😴', group: 'leave' },
  { value: '오전반차', label: '오전반차', icon: '😴', group: 'leave' },
  { value: '오후반차', label: '오후반차', icon: '😴', group: 'leave' },
];
const TRIP_TYPE_COLOR: Record<string, { bg: string; border: string; text: string }> = {
  out:   { bg: '#eef2ff', border: '#6366f1', text: '#4f46e5' },
  in:    { bg: '#f0fdf4', border: '#22c55e', text: '#16a34a' },
  leave: { bg: '#fdf4ff', border: '#a855f7', text: '#9333ea' },
};
function isTripOutdoor(wt: string | undefined) {
  return !wt || (TRIP_WORK_TYPES.find(t => t.value === wt)?.group ?? 'out') === 'out';
}

interface TreatmentItem { id?: number; schedule_id?: number; round_no: number; treatment_date: string; note: string; }
interface SurveyItem { id?: number; schedule_id?: number; round_no: number; survey_type?: string; survey_date: string; note: string; }
interface Schedule {
  id: number; seq_no: number | null; test_number: string; title: string;
  test_year: string; company_id: number | null; category_id: number | null;
  location_id: number | null; field_owner_id: number | null;
  company_name: string; company_code: string; category_name: string; location_name: string;
  field_owner_name: string; field_owner_phone: string;
  start_date: string; field_address: string; planting_date: string;
  summer_winter: string; registration_type: string;
  test_start_date: string; test_end_date: string;
  report_date: string; cleanup: string; tims_status: string;
  review_record: string; qrcode: string; status: string;
  has_result_photo: boolean | null;
  partner_confirmed_at: string | null; partner_confirmed_by: string | null;
  treatments: TreatmentItem[]; surveys: SurveyItem[];
  created_at: string; updated_at: string;
}
interface FilterOption { id: number; name: string; code?: string; phone?: string; }
interface Filters {
  years: string[]; companies: FilterOption[]; categories: FilterOption[];
  locations: FilterOption[]; owners: FilterOption[];
}
interface Stats {
  total: number; by_year: { name: string; count: number }[];
  by_category: { name: string; count: number }[];
  by_company: { name: string; count: number }[];
  by_location: { name: string; count: number }[];
  by_status: { name: string; count: number }[];
}

const STATUS_LIST = ['처리완료', '조사완료', '성적서작성중', '성적서완료', '발급완료', '최종종료'] as const;
const statusColors: Record<string, string> = {
  '처리완료': 'processing',
  '조사완료': 'warning',
  '성적서작성중': 'processing',
  '성적서완료': 'success',
  '발급완료': 'success',
  '최종종료': 'default',
};
const statusTagColors: Record<string, string> = {
  '시작전':    '#94a3b8',
  '처리완료': '#3b82f6',
  '조사완료': '#f59e0b',
  '성적서작성중': '#8b5cf6',
  '성적서완료': '#10b981',
  '발급완료': '#0ea5e9',
  '최종종료': '#64748b',
};
const categoryColors: Record<string, string> = {
  '품목등록-위탁': '#6366f1', '품목등록-자체': '#0ea5e9', '기초시험': '#10b981', '지역적응성시험': '#f59e0b',
  '살균제': '#f472b6', '살균': '#f472b6',   // 분홍
  '살충제': '#22c55e', '살충': '#22c55e',   // 초록
  '제초제': '#eab308', '제초': '#eab308',   // 노랑
};
const getCategoryColor = (name: string) => categoryColors[name] || '#6366f1';

const SchedulePage: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({ years: [], companies: [], categories: [], locations: [], owners: [] });
  const [stats, setStats] = useState<Stats | null>(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterPeriod, setFilterPeriod] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<number>(0);
  const [filterCompany, setFilterCompany] = useState<number>(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [activeTab, setActiveTab] = useState('table');
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState<Schedule | null>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [treatmentModalVisible, setTreatmentModalVisible] = useState(false);
  const [surveyModalVisible, setSurveyModalVisible] = useState(false);
  const [surveyModalType, setSurveyModalType] = useState<'약효' | '약해'>('약효');
  const [itemModalSchedule, setItemModalSchedule] = useState<Schedule | null>(null);
  const [treatmentForm] = Form.useForm();
  const [surveyForm] = Form.useForm();
  const [itemModalSaving, setItemModalSaving] = useState(false);

  // 출장 등록 모달
  const [tripModalVisible, setTripModalVisible] = useState(false);
  const [tripForm] = Form.useForm();
  const [tripSaving, setTripSaving] = useState(false);
  const [tripResearchers, setTripResearchers] = useState<string[]>([]);
  const [tripVehicles, setTripVehicles] = useState<string[]>([]);
  const tripWatchedWorkType = Form.useWatch('work_type', tripForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, filterRes, statsRes] = await Promise.all([
        scheduleAPI.list({ search, test_year: filterPeriod ? undefined : filterYear, category_id: filterCategory || undefined, company_id: filterCompany || undefined, status: filterStatus || undefined }),
        scheduleAPI.filters(),
        scheduleAPI.stats(),
      ]);
      setSchedules(listRes.data);
      setFilters(filterRes.data);
      setStats(statsRes.data);
    } catch (err: any) { message.error(err.response?.data?.detail || '데이터를 불러오는데 실패했습니다.'); }
    finally { setLoading(false); }
  }, [search, filterYear, filterPeriod, filterCategory, filterCompany, filterStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 출장 마스터 데이터 로드
  useEffect(() => {
    masterAPI.researchers.list().then(r => setTripResearchers(r.data.map((x: any) => x.name))).catch(() => {});
    masterAPI.vehicles.list().then(r => setTripVehicles(r.data.map((x: any) => x.vehicle_number))).catch(() => {});
  }, []);

  const openTripModal = (record: Schedule) => {
    tripForm.resetFields();
    const loginUser = getUsername() || '';
    tripForm.setFieldsValue({
      work_type: '외근',
      test_number: record.test_number,
      date_range: [dayjs(), dayjs()],
      researchers: loginUser ? [loginUser] : [],
    });
    setTripModalVisible(true);
  };

  const handleTripSave = async () => {
    try {
      const vals = await tripForm.validateFields();
      setTripSaving(true);
      const dr = vals.date_range;
      const start = dr?.[0] || null;
      const end = dr?.[1] || null;
      const researchers = Array.isArray(vals.researchers)
        ? vals.researchers.join(', ') : (vals.researchers || '');
      const vehicles = Array.isArray(vals.vehicle_number)
        ? vals.vehicle_number.join(', ') : (vals.vehicle_number || null);
      await businessTripAPI.create({
        ...vals,
        researchers,
        vehicle_number: vehicles,
        trip_start_date: start ? start.format('YYYY-MM-DDTHH:mm:ss') : null,
        trip_end_date: end ? end.format('YYYY-MM-DDTHH:mm:ss') : (start ? start.format('YYYY-MM-DDTHH:mm:ss') : null),
        trip_date_raw: start ? start.format('YYYY-MM-DD') : null,
        supervisor_confirmed_at: vals.supervisor_confirmed_at
          ? vals.supervisor_confirmed_at.format('YYYY-MM-DDTHH:mm:ss') : null,
      });
      message.success('출장이 등록되었습니다.');
      setTripModalVisible(false);
    } catch (e: any) {
      if (e?.response?.data?.detail) message.error(e.response.data.detail);
    } finally {
      setTripSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await scheduleAPI.delete(id); message.success('삭제되었습니다.'); fetchData();
  };

  const handleUpload = async (file: File) => {
    try {
      const { data } = await scheduleAPI.uploadExcel(file);
      const mc = data.master_created;
      message.success(`${data.message} | 마스터 자동생성: 의뢰사 ${mc.companies}, 장소 ${mc.locations}, 구분 ${mc.categories}, 포장주 ${mc.owners}`);
      fetchData();
    } catch (err: any) { message.error(err.response?.data?.detail || '업로드 실패'); }
    return false;
  };

  const handleExport = async () => {
    try {
      const res = await scheduleAPI.exportExcel(filterYear);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `시험일정총괄표_${filterYear || '전체'}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (err: any) { message.error(err.response?.data?.detail || 'Excel 다운로드에 실패했습니다.'); }
  };

  const handleTemplateDownload = async () => {
    try {
      const res = await scheduleAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = '시험일정총괄표_양식.xlsx';
      a.click(); window.URL.revokeObjectURL(url);
    } catch (err: any) { message.error(err.response?.data?.detail || '양식 다운로드에 실패했습니다.'); }
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      // dayjs → 문자열 변환 (start_date는 텍스트 입력)
      values.planting_date = fromDay(values.planting_date);
      values.report_date = fromDay(values.report_date);
      values.tims_status = fromDay(values.tims_status);
      // 처리/조사 배열 정리 + dayjs 변환
      values.treatments = (values.treatments || [])
        .map((t: any) => ({ ...t, treatment_date: fromDay(t?.treatment_date) }))
        .filter((t: any) => t && t.treatment_date);
      const yakho = (values.surveys_yakho || [])
        .map((s: any) => ({ ...s, survey_date: fromDay(s?.survey_date), survey_type: '약효' }))
        .filter((s: any) => s && s.survey_date);
      const yakhe = (values.surveys_yakhe || [])
        .map((s: any) => ({ ...s, survey_date: fromDay(s?.survey_date), survey_type: '약해' }))
        .filter((s: any) => s && s.survey_date);
      values.surveys = [...yakho, ...yakhe];
      // Date order validation warning
      if (values.start_date && values.report_date) {
        const startStr = values.start_date as string;
        const reportStr = typeof values.report_date === 'string' ? values.report_date : values.report_date.format('YYYY-MM-DD');
        if (startStr > reportStr) {
          message.warning('시작일이 보고서 제출일보다 늦습니다. 날짜를 확인해주세요.');
        }
      }
      if (editingId) {
        await scheduleAPI.update(editingId, values); message.success('수정되었습니다.');
      } else {
        await scheduleAPI.create(values); message.success('등록되었습니다.');
      }
      setFormVisible(false); form.resetFields(); setEditingId(null); fetchData();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    }
  };

  const [timelineVisible, setTimelineVisible] = useState(false);
  const [timelineRecord, setTimelineRecord] = useState<Schedule | null>(null);
  const [statusChanging, setStatusChanging] = useState(false);

  const handleQuickStatus = async (newStatus: string) => {
    if (!editingId) return;
    setStatusChanging(true);
    try {
      const values = await form.validateFields();
      // start_date는 텍스트 입력 — dayjs 변환 불필요
      values.planting_date = fromDay(values.planting_date);
      values.report_date   = fromDay(values.report_date);
      values.tims_status   = fromDay(values.tims_status);
      values.treatments = (values.treatments || [])
        .map((t: any) => ({ ...t, treatment_date: fromDay(t?.treatment_date) }))
        .filter((t: any) => t && t.treatment_date);
      values.surveys = (values.surveys || [])
        .map((s: any) => ({ ...s, survey_date: fromDay(s?.survey_date) }))
        .filter((s: any) => s && s.survey_date);
      values.status = newStatus;
      await scheduleAPI.update(editingId, values);
      form.setFieldValue('status', newStatus);
      message.success(
        <span>상태가 <strong style={{ color: statusTagColors[newStatus] }}>{newStatus}</strong>(으)로 변경되었습니다.</span>
      );
      fetchData();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    } finally { setStatusChanging(false); }
  };

  const openEdit = (record: Schedule) => {
    setEditingId(record.id);
    const vals: any = { ...record };
    // 문자열 → dayjs 변환 (start_date는 텍스트 입력 — 변환 불필요)
    vals.planting_date = toDay(vals.planting_date);
    vals.report_date = toDay(vals.report_date);
    vals.tims_status = toDay(vals.tims_status);
    // treatments/surveys 일자 변환
    vals.treatments = (vals.treatments || []).map((t: any) => ({ ...t, treatment_date: toDay(t.treatment_date) }));
    const allSurveys = (vals.surveys || []).map((s: any) => ({ ...s, survey_date: toDay(s.survey_date) }));
    vals.surveys_yakho = allSurveys.filter((s: any) => !s.survey_type || s.survey_type === '약효');
    vals.surveys_yakhe = allSurveys.filter((s: any) => s.survey_type === '약해');
    if (!vals.treatments?.length) vals.treatments = [{ round_no: 1, treatment_date: null, note: '' }];
    if (!vals.surveys_yakho?.length) vals.surveys_yakho = [{ round_no: 1, survey_date: null, note: '' }];
    if (!vals.surveys_yakhe?.length) vals.surveys_yakhe = [{ round_no: 1, survey_date: null, note: '' }];
    form.setFieldsValue(vals);
    setFormVisible(true);
  };

  const openNew = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      treatments: [{ round_no: 1, treatment_date: null, note: '' }],
      surveys_yakho: [{ round_no: 1, survey_date: null, note: '' }],
      surveys_yakhe: [{ round_no: 1, survey_date: null, note: '' }],
      status: '처리완료',
    });
    setFormVisible(true);
  };

  const buildSchedulePayload = (record: Schedule, overrides: object) => ({
    seq_no: record.seq_no,
    test_number: record.test_number,
    title: record.title,
    test_year: record.test_year,
    company_id: record.company_id,
    category_id: record.category_id,
    location_id: record.location_id,
    field_owner_id: record.field_owner_id,
    status: record.status,
    start_date: record.start_date,
    field_address: record.field_address,
    planting_date: record.planting_date,
    report_date: record.report_date,
    cleanup: record.cleanup,
    tims_status: record.tims_status,
    review_record: record.review_record,
    qrcode: record.qrcode,
    treatments: record.treatments,
    surveys: record.surveys,
    ...overrides,
  });

  const openTreatmentModal = (record: Schedule) => {
    setItemModalSchedule(record);
    treatmentForm.setFieldsValue({
      treatments: record.treatments.length
        ? record.treatments.map(t => ({ ...t, treatment_date: toDay(t.treatment_date) }))
        : [{ round_no: 1, treatment_date: null, note: '' }],
    });
    setTreatmentModalVisible(true);
  };

  const openSurveyModal = (record: Schedule, type: '약효' | '약해') => {
    setItemModalSchedule(record);
    setSurveyModalType(type);
    const all = record.surveys.map(s => ({ ...s, survey_date: toDay(s.survey_date) }));
    const yakho = all.filter(s => !s.survey_type || s.survey_type === '약효');
    const yakhe = all.filter(s => s.survey_type === '약해');
    surveyForm.setFieldsValue({
      surveys_yakho: yakho.length ? yakho : [{ round_no: 1, survey_date: null, note: '' }],
      surveys_yakhe: yakhe.length ? yakhe : [{ round_no: 1, survey_date: null, note: '' }],
    });
    setSurveyModalVisible(true);
  };

  const handleTreatmentSave = async () => {
    if (!itemModalSchedule) return;
    setItemModalSaving(true);
    try {
      const values = await treatmentForm.validateFields();
      const treatments = (values.treatments || [])
        .map((t: any) => ({ ...t, treatment_date: fromDay(t.treatment_date) }))
        .filter((t: any) => t.treatment_date);
      await scheduleAPI.update(itemModalSchedule.id, buildSchedulePayload(itemModalSchedule, { treatments }));
      message.success('처리일정이 저장되었습니다.');
      setTreatmentModalVisible(false);
      fetchData();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    } finally { setItemModalSaving(false); }
  };

  const handleSurveySave = async () => {
    if (!itemModalSchedule) return;
    setItemModalSaving(true);
    try {
      const values = await surveyForm.validateFields();
      // 현재 편집 중인 type만 저장, 나머지 type은 기존 데이터 보존
      const existingAll = itemModalSchedule.surveys || [];
      let surveys: any[] = [];
      if (surveyModalType === '약효') {
        const preserved = existingAll
          .filter(s => s.survey_type === '약해')
          .map(s => ({ ...s, survey_type: '약해' }));
        const updated = (values.surveys_yakho || [])
          .map((s: any) => ({ ...s, survey_date: fromDay(s.survey_date), survey_type: '약효' }))
          .filter((s: any) => s.survey_date);
        surveys = [...updated, ...preserved];
      } else {
        const preserved = existingAll
          .filter(s => !s.survey_type || s.survey_type === '약효')
          .map(s => ({ ...s, survey_type: '약효' }));
        const updated = (values.surveys_yakhe || [])
          .map((s: any) => ({ ...s, survey_date: fromDay(s.survey_date), survey_type: '약해' }))
          .filter((s: any) => s.survey_date);
        surveys = [...preserved, ...updated];
      }
      await scheduleAPI.update(itemModalSchedule.id, buildSchedulePayload(itemModalSchedule, { surveys }));
      message.success(`${surveyModalType === '약효' ? '약효' : '약해'}조사일정이 저장되었습니다.`);
      setSurveyModalVisible(false);
      fetchData();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    } finally { setItemModalSaving(false); }
  };

  const columns = useMemo(() => [
    { title: '연번', dataIndex: 'seq_no', width: 55, fixed: 'left' as const,
      sorter: (a: Schedule, b: Schedule) => (a.seq_no || 0) - (b.seq_no || 0) },
    { title: '시험번호', dataIndex: 'test_number', width: 145, fixed: 'left' as const,
      sorter: (a: Schedule, b: Schedule) => a.test_number.localeCompare(b.test_number),
      render: (v: string) => <Typography.Text strong style={{ color: brand.primary, fontSize: 12 }}>{v}</Typography.Text> },
    { title: '항목명', dataIndex: 'title', width: 260, ellipsis: true,
      sorter: (a: Schedule, b: Schedule) => (a.title || '').localeCompare(b.title || '', 'ko'),
      render: (v: string, r: Schedule) => <Tooltip title={v}><a onClick={() => { setDetailRecord(r); setDetailVisible(true); }}>{v}</a></Tooltip> },
    { title: '의뢰사', dataIndex: 'company_name', width: 90,
      sorter: (a: Schedule, b: Schedule) => (a.company_name || '').localeCompare(b.company_name || '', 'ko'),
      render: (v: string) => v ? <Tag color="blue" style={{ fontWeight: 600 }}>{v}</Tag> : '-' },
    { title: '구분', dataIndex: 'category_name', width: 80,
      sorter: (a: Schedule, b: Schedule) => (a.category_name || '').localeCompare(b.category_name || '', 'ko'),
      render: (v: string) => v
        ? <Tag style={{ fontWeight: 600, borderColor: getCategoryColor(v), color: getCategoryColor(v), background: getCategoryColor(v) + '18' }}>{v}</Tag>
        : '-' },
    { title: '작기', dataIndex: 'summer_winter', width: 56, align: 'center' as const,
      render: (v: string) => <SeasonBadge value={v} /> },
    {
      title: '처리',
      key: 'treatment_progress',
      width: 70,
      align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const treatments = record.treatments || [];
        const total = treatments.length;
        const filled = treatments.filter((t: any) => t.treatment_date).length;
        const color = total === 0 ? '#94a3b8' : filled === total ? '#10b981' : '#f59e0b';
        return (
          <Tooltip title="처리일정 보기/편집">
            <a style={{ fontSize: 12, fontWeight: 600, color }} onClick={() => openTreatmentModal(record)}>
              {filled}/{total}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: '약효조사',
      key: 'survey_yakho',
      width: 72,
      align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const surveys = (record.surveys || []).filter((s: any) => !s.survey_type || s.survey_type === '약효');
        const total = surveys.length;
        const filled = surveys.filter((s: any) => s.survey_date).length;
        const color = total === 0 ? '#94a3b8' : filled === total ? '#10b981' : '#f59e0b';
        return (
          <Tooltip title="약효조사일정 등록/수정">
            <a style={{ fontSize: 12, fontWeight: 600, color }}
              onClick={e => { e.stopPropagation(); openSurveyModal(record, '약효'); }}>
              {filled}/{total}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: '약해조사',
      key: 'survey_yakhe',
      width: 72,
      align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const surveys = (record.surveys || []).filter((s: any) => s.survey_type === '약해');
        const total = surveys.length;
        const filled = surveys.filter((s: any) => s.survey_date).length;
        if (total === 0) {
          return (
            <Tooltip title="약해조사일정 등록">
              <a style={{ fontSize: 11, color: '#cbd5e1' }}
                onClick={e => { e.stopPropagation(); openSurveyModal(record, '약해'); }}>
                + 등록
              </a>
            </Tooltip>
          );
        }
        const color = filled === total ? '#ea580c' : '#f97316';
        return (
          <Tooltip title="약해조사일정 등록/수정">
            <a style={{ fontSize: 12, fontWeight: 600, color }}
              onClick={e => { e.stopPropagation(); openSurveyModal(record, '약해'); }}>
              {filled}/{total}
            </a>
          </Tooltip>
        );
      },
    },
    { title: '상태', dataIndex: 'status', width: 90, fixed: 'right' as const,
      sorter: (a: Schedule, b: Schedule) => (a.status || '').localeCompare(b.status || '', 'ko'),
      render: (v: string, record: Schedule) => {
        const hasTreatment = record.treatments?.some(t => t.treatment_date);
        const label = !hasTreatment ? '시작전' : (v || '-');
        return <Tag color={statusTagColors[label] || '#64748b'} style={{ fontSize: 11, padding: '0 6px' }}>{label}</Tag>;
      } },
    { title: '', width: 140, fixed: 'right' as const,
      render: (_: any, record: Schedule) => (
        <Space size={4}>
          <Tooltip title="진행 타임라인"><Button size="small" type="text" icon={<HistoryOutlined style={{ color: '#8b5cf6' }} />} onClick={() => { setTimelineRecord(record); setTimelineVisible(true); }} /></Tooltip>
          <Tooltip title="상세"><Button size="small" type="text" icon={<EyeOutlined />} onClick={() => { setDetailRecord(record); setDetailVisible(true); }} /></Tooltip>
          <Tooltip title="업무 및 출장 등록"><Button size="small" type="text" icon={<CarOutlined style={{ color: brand.primary }} />} onClick={() => openTripModal(record)} /></Tooltip>
          <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} /></Tooltip>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(record.id)} okText="삭제" cancelText="취소">
            <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ], []);

  const companyChart = useMemo(() => stats ? {
    title: { text: '의뢰사별', left: 'center', textStyle: { fontSize: 14, color: gray[700], fontWeight: 600 } },
    tooltip: { trigger: 'item' as const },
    series: [{ type: 'pie' as const, radius: ['35%','65%'], data: stats.by_company.map(d => ({ name: d.name, value: d.count })),
      itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 }, label: { fontSize: 11, color: gray[500] },
      color: ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#f97316','#14b8a6','#e879f9','#fb923c'] }],
  } : {}, [stats]);
  const categoryChart = useMemo(() => stats ? {
    title: { text: '구분별', left: 'center', textStyle: { fontSize: 14, color: gray[700], fontWeight: 600 } },
    tooltip: { trigger: 'axis' as const }, grid: { top: 50, bottom: 30, left: 60, right: 20 },
    xAxis: { type: 'category' as const, data: stats.by_category.map(d => d.name), axisLabel: { color: gray[500], fontSize: 11 } },
    yAxis: { type: 'value' as const, minInterval: 1 },
    series: [{ data: stats.by_category.map(d => d.count), type: 'bar' as const, barWidth: '50%',
      itemStyle: { color: { type: 'linear', x:0,y:0,x2:0,y2:1, colorStops:[{offset:0,color:'#6366f1'},{offset:1,color:'#818cf8'}]} as any, borderRadius:[6,6,0,0] } }],
  } : {}, [stats]);
  const locationChart = useMemo(() => stats ? {
    title: { text: '시험장소별 (상위 15)', left: 'center', textStyle: { fontSize: 14, color: gray[700], fontWeight: 600 } },
    tooltip: { trigger: 'axis' as const }, grid: { top: 50, bottom: 80, left: 50, right: 20 },
    xAxis: { type: 'category' as const, data: stats.by_location.sort((a,b) => b.count - a.count).slice(0,15).map(d => d.name), axisLabel: { color: gray[500], fontSize: 10, rotate: 45 } },
    yAxis: { type: 'value' as const, minInterval: 1 },
    series: [{ data: stats.by_location.sort((a,b) => b.count - a.count).slice(0,15).map(d => d.count), type: 'bar' as const, barWidth: '60%', itemStyle: { color: '#0ea5e9', borderRadius:[4,4,0,0] } }],
  } : {}, [stats]);

  const activeFilterCount = [filterYear, filterCategory, filterCompany, filterStatus].filter(Boolean).length;
  const clearAllFilters = () => { setFilterYear(''); setFilterCategory(0); setFilterCompany(0); setFilterStatus(''); setSearchText(''); setFilterPeriod(null); };

  // 퀵필터 기간 정의 (당년/전년 동적 계산)
  const _now = new Date();
  const currYY = String(_now.getFullYear() % 100).padStart(2, '0');        // '26'
  const prevYY = String((_now.getFullYear() - 1) % 100).padStart(2, '0'); // '25'
  const PERIOD_PRESETS = [
    { key: 'prev_h',  label: `${prevYY}년 하작물`,          years: [prevYY, `20${prevYY}`], sw: ['S','E','s','e'] },
    { key: 'prev_d',  label: `${prevYY}~${currYY}년 동작물`, years: [prevYY, `20${prevYY}`, currYY, `20${currYY}`], sw: ['W','동','w'] },
    { key: 'curr_h',  label: `${currYY}년 하작물`,          years: [currYY, `20${currYY}`], sw: ['S','E','s','e'] },
    { key: 'curr_d',  label: `${currYY}년 동작물`,          years: [currYY, `20${currYY}`], sw: ['W','동','w'] },
  ];

  const displayedSchedules = useMemo(() => {
    if (!filterPeriod) return schedules;
    const preset = PERIOD_PRESETS.find(p => p.key === filterPeriod);
    if (!preset) return schedules;
    return schedules.filter(s => {
      // test_year 필드 + test_number에서 연도 추출(SR26-... → '26') 으로 매칭
      const yr = (s.test_year || '').trim();
      const tnYr = (s.test_number || '').match(/SR(\d{2})/i)?.[1] || '';
      const sw = (s.summer_winter || '').trim();
      const yearMatch = preset.years.includes(yr) || preset.years.includes(tnYr);
      const swMatch = preset.sw.includes(sw);
      return yearMatch && swMatch;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules, filterPeriod]);

  const statCards = [
    {
      label: '총 시험일정', value: stats?.total || 0, suffix: '건',
      color: brand.primary, bg: 'linear-gradient(135deg,#ede9fe,#f5f3ff)', icon: <CalendarOutlined />,
      tooltip: '현재 필터 조건에 관계없이 시스템에 등록된 전체 시험일정 건수입니다.',
    },
    ...(stats?.by_year.slice(0, 3).map(y => ({
      label: y.name, value: y.count, suffix: '건',
      color: '#0ea5e9', bg: 'linear-gradient(135deg,#e0f2fe,#f0f9ff)', icon: <AppstoreOutlined />,
      tooltip: `연도 코드 '${y.name}'(20${y.name}년)으로 등록된 시험일정 건수입니다. 하절기·동절기 작기 구분 없이 해당 연도 전체를 집계합니다.`,
    })) || []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── 통계 카드 ── */}
      <Row gutter={[12, 12]}>
        {statCards.map((s, i) => (
          <Col xs={12} sm={6} key={i}>
            <div style={{
              background: '#fff', borderRadius: 12, padding: '14px 18px',
              border: '1px solid #f1f5f9', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              display: 'flex', alignItems: 'center', gap: 14, position: 'relative',
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10, background: s.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, color: s.color, flexShrink: 0,
              }}>
                {s.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span style={{ fontSize: 11, color: gray[400], fontWeight: 500 }}>{s.label}</span>
                  {s.tooltip && (
                    <Tooltip title={s.tooltip} overlayStyle={{ maxWidth: 260 }} placement="top">
                      <QuestionCircleOutlined style={{ fontSize: 11, color: gray[300], cursor: 'pointer', flexShrink: 0 }} />
                    </Tooltip>
                  )}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: gray[800], lineHeight: 1 }}>
                  {s.value}<span style={{ fontSize: 12, fontWeight: 500, color: gray[400], marginLeft: 3 }}>{s.suffix}</span>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ── 메인 카드 ── */}
      <Card bodyStyle={{ padding: '0' }} style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}
          tabBarStyle={{ margin: 0, padding: '0 20px', background: '#fafbfc', borderBottom: 'none' }}
          renderTabBar={(props, DefaultTabBar) => (
            <div>
              <DefaultTabBar {...(props as any)} style={{ borderBottom: '1px solid #e2e8f0' }} />
              {/* ── 작기 필터 바 ── */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 20px',
                background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
                borderBottom: '2px solid #e2e8f0',
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, color: gray[400],
                  letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: 2,
                }}>
                  작기
                </span>
                <div style={{ width: 1, height: 18, background: '#cbd5e1', margin: '0 4px' }} />
                {/* 전체 버튼 */}
                <button
                  onClick={() => { setFilterPeriod(null); setFilterYear(String(new Date().getFullYear())); }}
                  style={{
                    padding: '5px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                    fontWeight: !filterPeriod ? 700 : 500,
                    border: `1.5px solid ${!filterPeriod ? '#64748b' : '#e2e8f0'}`,
                    background: !filterPeriod ? '#334155' : '#fff',
                    color: !filterPeriod ? '#fff' : '#94a3b8',
                    boxShadow: !filterPeriod ? '0 2px 8px rgba(51,65,85,0.25)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  전체
                </button>
                {[
                  { key: 'prev_h', label: `${prevYY}년 하작물`,           isDong: false, color: '#d97706', hoverBg: '#fef3c7', gradBg: 'linear-gradient(135deg,#fbbf24,#d97706)' },
                  { key: 'prev_d', label: `${prevYY}~${currYY}년 동작물`, isDong: true,  color: brand.primary, hoverBg: '#eef2ff', gradBg: 'linear-gradient(135deg,#818cf8,#4f46e5)' },
                  { key: 'curr_h', label: `${currYY}년 하작물`,           isDong: false, color: '#059669', hoverBg: '#ecfdf5', gradBg: 'linear-gradient(135deg,#34d399,#059669)' },
                  { key: 'curr_d', label: `${currYY}년 동작물`,           isDong: true,  color: '#0369a1', hoverBg: '#e0f2fe', gradBg: 'linear-gradient(135deg,#38bdf8,#0369a1)' },
                ].map(p => {
                  const isActive = filterPeriod === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => {
                        if (isActive) { setFilterPeriod(null); setFilterYear(String(new Date().getFullYear())); }
                        else { setFilterPeriod(p.key); setFilterYear(''); }
                      }}
                      style={{
                        padding: '5px 14px', borderRadius: 20, fontSize: 12.5, cursor: 'pointer',
                        fontWeight: isActive ? 700 : 500,
                        border: `1.5px solid ${isActive ? 'transparent' : p.color + '55'}`,
                        background: isActive ? p.gradBg : p.hoverBg,
                        color: isActive ? '#fff' : p.color,
                        boxShadow: isActive ? `0 3px 10px ${p.color}45` : 'none',
                        transition: 'all 0.15s ease',
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                      }}
                    >
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        background: isActive ? 'rgba(255,255,255,0.25)' : (p.isDong ? '#dbeafe' : '#dcfce7'),
                        border: `1px solid ${isActive ? 'rgba(255,255,255,0.45)' : (p.isDong ? '#93c5fd' : '#86efac')}`,
                        borderRadius: 8, padding: '1px 5px',
                        fontSize: 10, fontWeight: 800,
                        color: isActive ? '#fff' : (p.isDong ? '#1d4ed8' : '#166534'),
                        lineHeight: 1.4,
                      }}>
                        <span style={{ fontSize: 10, lineHeight: 1 }}>{p.isDong ? '🧊' : '☀️'}</span>
                        {p.isDong ? '동' : '하'}
                      </span>
                      {p.label}
                      {isActive && (
                        <span style={{
                          marginLeft: 2, fontSize: 10, opacity: 0.85,
                          background: 'rgba(255,255,255,0.25)', borderRadius: '50%',
                          width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}>✕</span>
                      )}
                    </button>
                  );
                })}
                {filterPeriod && (
                  <span style={{
                    marginLeft: 6, fontSize: 11, color: gray[500],
                    background: '#f1f5f9', borderRadius: 10, padding: '2px 8px',
                  }}>
                    {displayedSchedules.length}건
                  </span>
                )}
              </div>
            </div>
          )}
          items={[
          { key: 'table', label: <span style={{ fontSize: 13, fontWeight: 500 }}><CalendarOutlined style={{ marginRight: 6 }} />시험일정 목록</span>, children: (
            <div style={{ padding: '16px 20px' }}>

              {/* ── 검색·필터 패널 ── */}
              <div style={{
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '14px 16px', marginBottom: 14,
              }}>
                {/* 상단: 검색 + 액션 버튼 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <Input
                    placeholder="시험번호, 항목명, 포장주소 검색..."
                    prefix={<SearchOutlined style={{ color: gray[400] }} />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                    style={{ width: 300, borderRadius: 8 }}
                  />
                  <div style={{ flex: 1 }} />
                  <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={(file) => { handleUpload(file); return false; }}>
                    <Button icon={<FileExcelOutlined />} size="middle" style={{ borderRadius: 8, borderColor: '#10b981', color: '#10b981' }}>
                      Excel 업로드
                    </Button>
                  </Upload>
                  <Button icon={<DownloadOutlined />} size="middle" onClick={handleExport}
                    style={{ borderRadius: 8, borderColor: brand.primary, color: brand.primary, fontWeight: 600 }}>
                    Excel 다운로드
                  </Button>
                  <Button icon={<FileExcelOutlined />} size="middle" onClick={handleTemplateDownload}
                    style={{ borderRadius: 8, borderColor: '#6366f1', color: '#6366f1' }}>
                    양식 다운로드
                  </Button>
                  <Button icon={<PlusOutlined />} type="primary" size="middle" onClick={openNew}
                    style={{ borderRadius: 8, background: 'linear-gradient(135deg,#4f46e5,#6366f1)', border: 'none', fontWeight: 600 }}>
                    신규 등록
                  </Button>
                  <Tooltip title="새로고침">
                    <Button icon={<ReloadOutlined />} size="middle" onClick={fetchData} style={{ borderRadius: 8 }} />
                  </Tooltip>
                </div>

                {/* 구분선 */}
                <Divider style={{ margin: '0 0 12px', borderColor: '#e2e8f0' }} />

                {/* 하단: 필터 셀렉터 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: gray[500], marginRight: 4 }}>
                    <FilterOutlined />필터
                  </span>
                  <Select
                    placeholder="📅 시험년도"
                    value={filterYear || undefined}
                    onChange={v => setFilterYear(v || '')}
                    allowClear size="small"
                    style={{ width: 120, borderRadius: 8 }}
                  >
                    {filters.years
                      .map(y => ({ raw: y, full: y.length === 2 ? `20${y}` : y }))
                      .sort((a, b) => b.full.localeCompare(a.full))
                      .map(({ raw, full }) => <Option key={raw} value={raw}>{`${full}년`}</Option>)}
                  </Select>
                  <Select
                    placeholder="🏷 구분"
                    value={filterCategory || undefined}
                    onChange={v => setFilterCategory(v || 0)}
                    allowClear size="small"
                    style={{ width: 155, borderRadius: 8 }}
                  >
                    {filters.categories
                      .filter(c => ['품목등록-위탁','품목등록-자체','기초시험','지역적응성시험'].includes(c.name))
                      .map(c => {
                        const label = c.name.startsWith('품목등록-') ? c.name.slice(5)
                          : c.name === '지역적응성시험' ? '지역적응성' : c.name;
                        return <Option key={c.id} value={c.id}>{label}</Option>;
                      })}
                  </Select>
                  <Select
                    placeholder="🏢 의뢰사"
                    value={filterCompany || undefined}
                    onChange={v => setFilterCompany(v || 0)}
                    allowClear size="small"
                    style={{ width: 120, borderRadius: 8 }}
                  >
                    {filters.companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                  </Select>
                  <Select
                    placeholder="🔵 상태"
                    value={filterStatus || undefined}
                    onChange={v => setFilterStatus(v || '')}
                    allowClear size="small"
                    style={{ width: 130, borderRadius: 8 }}
                  >
                    {STATUS_LIST.map(s => (
                      <Option key={s} value={s}>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                          <span style={{ width:8, height:8, borderRadius:'50%', background: statusTagColors[s], display:'inline-block', flexShrink:0 }} />
                          {s}
                        </span>
                      </Option>
                    ))}
                  </Select>

                  {activeFilterCount > 0 && (
                    <Button
                      size="small" type="link" danger
                      icon={<CloseCircleOutlined />}
                      onClick={clearAllFilters}
                      style={{ fontSize: 12, padding: '0 6px' }}
                    >
                      필터 초기화 ({activeFilterCount})
                    </Button>
                  )}

                  <div style={{ marginLeft: 'auto', fontSize: 12, color: gray[400] }}>
                    {displayedSchedules.length}건 조회됨
                  </div>
                </div>
              </div>

              {/* ── 테이블 ── */}
              <Table
                dataSource={displayedSchedules}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                showSorterTooltip={false}
                scroll={{ x: 1220, y: 'calc(100vh - 500px)' }}
                sticky
                rowClassName={(_, index) => index % 2 === 0 ? '' : 'table-row-alt'}
                locale={{ emptyText: <EmptyState title="등록된 시험일정이 없습니다" size="sm" /> }}
                style={{ borderRadius: 8, overflow: 'hidden' }}
                pagination={{
                  defaultPageSize: 50, showSizeChanger: true,
                  pageSizeOptions: ['20','50','100','200'],
                  showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
                  style: { marginTop: 12 },
                }}
              />
            </div>
          )},
          { key: 'stats', label: <span style={{ fontSize: 13, fontWeight: 500 }}><BarChartOutlined style={{ marginRight: 6 }} />통계 분석</span>, children: stats ? (
            <div style={{ padding: '16px 20px' }}>
              <Row gutter={[16,16]}>
                <Col xs={24} lg={12}><Card size="small" style={{ borderRadius: 10 }}><ReactECharts option={companyChart} style={{ height: 320 }} /></Card></Col>
                <Col xs={24} lg={12}><Card size="small" style={{ borderRadius: 10 }}><ReactECharts option={categoryChart} style={{ height: 320 }} /></Card></Col>
                <Col xs={24}><Card size="small" style={{ borderRadius: 10 }}><ReactECharts option={locationChart} style={{ height: 350 }} /></Card></Col>
              </Row>
            </div>
          ) : null },
        ]} />
      </Card>

      {/* 상세보기 모달 */}
      <Modal
        title={detailRecord ? (
          <Space>
            <Typography.Text strong style={{ color: brand.primary }}>{detailRecord.test_number}</Typography.Text>
            <Typography.Text>{detailRecord.title}</Typography.Text>
          </Space>
        ) : ''}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={820}
        maskClosable={false}
        styles={{ body: { maxHeight: '72vh', overflowY: 'auto', paddingRight: 4 } }}
      >
        {detailRecord && (() => {
          const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: gray[400], marginBottom: 3, fontWeight: 500 }}>{label}</div>
              <div style={{ fontSize: 13, color: gray[800], minHeight: 20 }}>{children || <span style={{ color: '#cbd5e1' }}>-</span>}</div>
            </div>
          );
          const SectionBar = ({ icon, label, color, bg }: { icon: React.ReactNode; label: string; color: string; bg: string }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', margin: '0 0 14px', background: bg, borderLeft: `3px solid ${color}`, borderRadius: '0 6px 6px 0' }}>
              {icon}<span style={{ fontWeight: 600, fontSize: 12, color }}>{label}</span>
            </div>
          );
          return (
            <div style={{ padding: '4px 0' }}>
              {/* ── 기본 정보 ── */}
              <SectionBar icon={<ExperimentOutlined style={{ color: brand.primary, fontSize: 13 }} />} label="기본 정보" color="#4f46e5" bg="#eef2ff" />
              <Row gutter={16}>
                <Col flex="80px"><Field label="연번">{detailRecord.seq_no ?? '-'}</Field></Col>
                <Col flex="1"><Field label="시험번호"><Typography.Text strong style={{ color: brand.primary }}>{detailRecord.test_number}</Typography.Text></Field></Col>
                <Col flex="120px"><Field label="시험년도">{detailRecord.test_year || '-'}</Field></Col>
              </Row>
              <Field label="항목명"><span style={{ fontWeight: 600 }}>{detailRecord.title}</span></Field>
              <Row gutter={16}>
                <Col span={8}><Field label="의뢰사">
                  {detailRecord.company_name
                    ? <Tag color="blue" style={{ fontWeight: 600 }}>{detailRecord.company_name}</Tag>
                    : '-'}
                </Field></Col>
                <Col span={8}><Field label="구분"><Tag style={{ fontWeight: 600, borderColor: getCategoryColor(detailRecord.category_name), color: getCategoryColor(detailRecord.category_name), background: getCategoryColor(detailRecord.category_name) + '1a' }}>{detailRecord.category_name || '-'}</Tag></Field></Col>
                <Col span={8}><Field label="상태"><Tag color={statusTagColors[detailRecord.status] || '#64748b'}>{detailRecord.status || '-'}</Tag></Field></Col>
              </Row>
              <Row gutter={16}>
                <Col span={9}><Field label="시험장소">{detailRecord.location_name || '-'}</Field></Col>
                <Col span={9}><Field label="포장주">{detailRecord.field_owner_name}{detailRecord.field_owner_phone ? ` (${detailRecord.field_owner_phone})` : ''}</Field></Col>
                <Col span={6}><Field label="개시일">{detailRecord.start_date || '-'}</Field></Col>
              </Row>
              <Row gutter={16}>
                <Col span={17}><Field label="시험포장주소">
                  <Space size={6}>
                    {detailRecord.field_address || '-'}
                    {detailRecord.field_address && (
                      <Tooltip title="네이버지도에서 검색">
                        <a href={`https://map.naver.com/v5/search/${encodeURIComponent(detailRecord.field_address)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#03C75A', fontSize: 15 }}><EnvironmentOutlined /></a>
                      </Tooltip>
                    )}
                  </Space>
                </Field></Col>
                <Col span={7}><Field label="정식시기">{detailRecord.planting_date || '-'}</Field></Col>
              </Row>

              {/* ── 처리일정 ── */}
              <SectionBar icon={<CalendarOutlined style={{ color: '#7c3aed', fontSize: 13 }} />} label={`처리일정 (${detailRecord.treatments.length}회)`} color="#7c3aed" bg="#f5f3ff" />
              <Table dataSource={detailRecord.treatments} rowKey="round_no" size="small" pagination={false} style={{ marginBottom: 16 }}
                columns={[
                  { title: '차수', dataIndex: 'round_no', width: 60, render: (v: number) => `${v}차` },
                  { title: '처리일자', dataIndex: 'treatment_date', width: 160 },
                  { title: '비고', dataIndex: 'note' },
                ]} />

              {/* ── 약효조사일정 ── */}
              {(() => {
                const yakho = (detailRecord.surveys || []).filter((s: any) => !s.survey_type || s.survey_type === '약효');
                return (<>
                  <SectionBar icon={<AuditOutlined style={{ color: '#0891b2', fontSize: 13 }} />} label={`약효조사일정 (${yakho.length}회)`} color="#0891b2" bg="#ecfeff" />
                  <Table dataSource={yakho} rowKey="id" size="small" pagination={false} style={{ marginBottom: 12 }}
                    columns={[
                      { title: '차수', dataIndex: 'round_no', width: 60, render: (v: number) => `${v}차` },
                      { title: '약효조사일자', dataIndex: 'survey_date', width: 160 },
                      { title: '비고', dataIndex: 'note' },
                    ]} />
                </>);
              })()}

              {/* ── 약해조사일정 ── */}
              {(() => {
                const yakhe = (detailRecord.surveys || []).filter((s: any) => s.survey_type === '약해');
                return (<>
                  <SectionBar icon={<AuditOutlined style={{ color: '#ea580c', fontSize: 13 }} />}
                    label={`약해조사일정 (${yakhe.length}회)`} color="#ea580c" bg="#fff7ed" />
                  <Table dataSource={yakhe} rowKey="id" size="small" pagination={false} style={{ marginBottom: 16 }}
                    columns={[
                      { title: '차수', dataIndex: 'round_no', width: 60, render: (v: number) => `${v}차` },
                      { title: '약해조사일자', dataIndex: 'survey_date', width: 160 },
                      { title: '비고', dataIndex: 'note' },
                    ]} />
                </>);
              })()}

              {/* ── 관리현황 ── */}
              <SectionBar icon={<FileDoneOutlined style={{ color: '#059669', fontSize: 13 }} />} label="관리현황" color="#059669" bg="#f0fdf4" />
              <Row gutter={16}>
                <Col span={6}><Field label="성적서발급일">{detailRecord.report_date || '-'}</Field></Col>
                <Col span={6}><Field label="TIMS입력현황">{detailRecord.tims_status || '-'}</Field></Col>
                <Col span={6}><Field label="정리">{detailRecord.cleanup || '-'}</Field></Col>
                <Col span={6}><Field label="검토기록지">{detailRecord.review_record || '-'}</Field></Col>
              </Row>
              {detailRecord.qrcode && (
                <div style={{ marginTop: 8, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 11, color: gray[500], marginBottom: 8, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 1 }}>
                    Dynamic QR 코드
                  </div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <img
                      src={qrAPI.imageUrl(detailRecord.qrcode)}
                      alt="QR"
                      style={{ width: 100, height: 100, border: '1px solid #e2e8f0', borderRadius: 6 }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div>
                        <span style={{ fontSize: 11, color: gray[400] }}>Short Code: </span>
                        <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: brand.primary, letterSpacing: 2 }}>
                          {detailRecord.qrcode}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: gray[400] }}>
                        api.sappt.co.kr/api/qr/{detailRecord.qrcode}
                      </div>
                      <Space size={6} style={{ marginTop: 2 }}>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => {
                            const a = document.createElement('a');
                            a.href = qrAPI.imageUrl(detailRecord.qrcode);
                            a.download = `qr_${detailRecord.qrcode}.png`;
                            a.click();
                          }}
                        >
                          다운로드
                        </Button>
                        <Button
                          size="small"
                          icon={<PrinterOutlined />}
                          type="primary"
                          ghost
                          onClick={() => {
                            const win = window.open('', '_blank', 'width=400,height=500');
                            if (!win) return;
                            win.document.write(`<!DOCTYPE html><html><head><title>QR 포장 표지판</title>
                              <style>body{font-family:sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;}
                              .label{border:2px solid #1e293b;border-radius:12px;padding:24px 32px;text-align:center;width:280px;}
                              .num{font-size:13px;color:#64748b;margin-bottom:4px;} .title{font-size:16px;font-weight:700;color:#1e293b;margin-bottom:16px;}
                              img{width:160px;height:160px;} .code{font-size:11px;color:#94a3b8;margin-top:8px;font-family:monospace;}
                              @media print{body{margin:0;}}</style></head><body>
                              <div class="label">
                                <div class="num">${detailRecord.test_number || ''}</div>
                                <div class="title">${detailRecord.title || ''}</div>
                                <img src="${window.location.origin}${qrAPI.imageUrl(detailRecord.qrcode)}" alt="QR"/>
                                <div class="code">api.sappt.co.kr/api/qr/${detailRecord.qrcode}</div>
                              </div>
                              <script>window.onload=()=>{window.print();}<\/script></body></html>`);
                            win.document.close();
                          }}
                        >
                          포장 표지판 인쇄
                        </Button>
                      </Space>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* 등록/수정 모달 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 4, height: 18, borderRadius: 2,
              background: editingId ? '#4f46e5' : '#059669', flexShrink: 0,
            }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: gray[800] }}>
              {editingId ? '시험일정 수정' : '시험일정 신규등록'}
            </span>
          </div>
        }
        open={formVisible}
        onCancel={() => { setFormVisible(false); setEditingId(null); form.resetFields(); }}
        onOk={handleFormSubmit}
        width={900}
        okText={editingId ? '수정 저장' : '등록'}
        cancelText="취소"
        maskClosable={false}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <Form form={form} layout="vertical" size="small" style={{ maxHeight: '78vh', overflow: 'auto', paddingRight: 4 }}>

          {/* ── 기본정보 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', margin: '0 0 8px',
            background: '#eef2ff', borderLeft: '3px solid #4f46e5', borderRadius: '0 6px 6px 0',
          }}>
            <ExperimentOutlined style={{ color: brand.primary, fontSize: 12 }} />
            <span style={{ fontWeight: 600, fontSize: 11, color: brand.primary }}>기본 정보</span>
          </div>

          {/* Row 1: 연번(60) | 시험번호(flex) | 시험년도(68) | 상태(130) */}
          <Row gutter={8} align="bottom">
            <Col flex="60px">
              <Form.Item name="seq_no" label="연번" style={{ marginBottom: 8 }}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col flex="1">
              <Form.Item name="test_number" label="시험번호" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                <Input />
              </Form.Item>
            </Col>
            <Col flex="68px">
              <Form.Item name="test_year" label="시험년도" style={{ marginBottom: 8 }}>
                <Input placeholder="25" />
              </Form.Item>
            </Col>
            <Col flex="128px">
              <Form.Item name="status" label="상태" initialValue="처리완료" style={{ marginBottom: 8 }}>
                <Select>
                  {STATUS_LIST.map(s => (
                    <Option key={s} value={s}>
                      <Tag color={statusTagColors[s]} style={{ fontSize: 11 }}>{s}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: 항목명 */}
          <Form.Item name="title" label="항목명" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
            <Input />
          </Form.Item>

          {/* Row 3: 의뢰사(6) | 구분(5) | 시험장소(7) | 개시일(6) */}
          <Row gutter={8}>
            <Col span={6}>
              <Form.Item name="company_id" label="의뢰사" style={{ marginBottom: 8 }}>
                <Select allowClear showSearch placeholder="선택"
                  filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
                  {filters.companies.map(c => <Option key={c.id} value={c.id}>{c.code ? `${c.code} - ${c.name}` : c.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="category_id" label="구분" style={{ marginBottom: 8 }}>
                <Select allowClear placeholder="선택">
                  {filters.categories.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="location_id" label="시험장소" style={{ marginBottom: 8 }}>
                <Select allowClear showSearch placeholder="선택"
                  filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
                  {filters.locations.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="start_date" label="개시일" style={{ marginBottom: 8 }}>
                <Input placeholder="9월 상순" />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4: 포장주(11) | 정식시기(7) | 정리(6) */}
          <Row gutter={8}>
            <Col span={11}>
              <Form.Item name="field_owner_id" label="포장주" style={{ marginBottom: 8 }}>
                <Select allowClear showSearch placeholder="선택"
                  filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
                  {filters.owners.map(o => <Option key={o.id} value={o.id}>{o.name}{o.phone ? ` (${o.phone})` : ''}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="planting_date" label="정식시기" style={{ marginBottom: 8 }}>
                <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="cleanup" label="정리" style={{ marginBottom: 8 }}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5: 시험포장주소 (전체 폭) */}
          <Form.Item name="field_address" label="시험포장주소" style={{ marginBottom: 8 }}>
            <Input />
          </Form.Item>

          {/* ── 처리일정 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 10px', margin: '4px 0 8px',
            background: '#f5f3ff', borderLeft: '3px solid #7c3aed', borderRadius: '0 6px 6px 0',
          }}>
            <Space size={6}>
              <CalendarOutlined style={{ color: '#7c3aed', fontSize: 12 }} />
              <span style={{ fontWeight: 600, fontSize: 11, color: '#7c3aed' }}>처리일정</span>
            </Space>
            <Form.Item noStyle shouldUpdate={(p, c) => p.status !== c.status}>
              {() => {
                const cur = form.getFieldValue('status');
                return (
                  <Tooltip title={cur === '처리완료' ? '이미 처리완료 상태입니다' : '저장 후 상태를 처리완료로 변경합니다'}>
                    <Button size="small" icon={<CheckCircleOutlined />} loading={statusChanging}
                      disabled={!editingId || cur === '처리완료'}
                      onClick={() => handleQuickStatus('처리완료')}
                      style={{
                        borderRadius: 6, fontSize: 11, fontWeight: 600, height: 24,
                        background: cur === '처리완료' ? '#eff6ff' : '#3b82f6',
                        borderColor: cur === '처리완료' ? '#bfdbfe' : '#3b82f6',
                        color: cur === '처리완료' ? '#93c5fd' : '#fff',
                      }}>처리완료</Button>
                  </Tooltip>
                );
              }}
            </Form.Item>
          </div>
          <Form.List name="treatments">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle" style={{ marginBottom: 0 }}>
                    <Col flex="64px">
                      <Form.Item {...rest} name={[name, 'round_no']} label={key === 0 ? '차수' : undefined} style={{ marginBottom: 6 }}>
                        <InputNumber min={1} max={7} style={{ width: 56 }} />
                      </Form.Item>
                    </Col>
                    <Col flex="160px">
                      <Form.Item {...rest} name={[name, 'treatment_date']} label={key === 0 ? '처리일자' : undefined} rules={[{ required: true, message: '필수' }]} style={{ marginBottom: 6 }}>
                        <DatePicker format={DATE_FORMAT} style={{ width: 148 }} placeholder="날짜 선택" />
                      </Form.Item>
                    </Col>
                    <Col flex="1">
                      <Form.Item {...rest} name={[name, 'note']} label={key === 0 ? '비고' : undefined} style={{ marginBottom: 6 }}>
                        <Input placeholder="비고" />
                      </Form.Item>
                    </Col>
                    <Col flex="28px">
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}
                        style={{ marginTop: key === 0 ? 22 : 0, padding: '0 4px' }} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, treatment_date: null, note: '' })}
                  icon={<PlusOutlined />} style={{ width: '100%', marginBottom: 4, height: 26, fontSize: 12 }}>처리 추가</Button>
              </>
            )}
          </Form.List>

          {/* ── 약효조사일정 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 10px', margin: '6px 0 8px',
            background: '#ecfeff', borderLeft: '3px solid #0891b2', borderRadius: '0 6px 6px 0',
          }}>
            <Space size={6}>
              <AuditOutlined style={{ color: '#0891b2', fontSize: 12 }} />
              <span style={{ fontWeight: 600, fontSize: 11, color: '#0891b2' }}>약효조사일정</span>
            </Space>
            <Form.Item noStyle shouldUpdate={(p, c) => p.status !== c.status}>
              {() => {
                const cur = form.getFieldValue('status');
                return (
                  <Tooltip title={cur === '조사완료' ? '이미 조사완료 상태입니다' : '저장 후 상태를 조사완료로 변경합니다'}>
                    <Button size="small" icon={<CheckCircleOutlined />} loading={statusChanging}
                      disabled={!editingId || cur === '조사완료'}
                      onClick={() => handleQuickStatus('조사완료')}
                      style={{
                        borderRadius: 6, fontSize: 11, fontWeight: 600, height: 24,
                        background: cur === '조사완료' ? '#fffbeb' : '#f59e0b',
                        borderColor: cur === '조사완료' ? '#fde68a' : '#f59e0b',
                        color: cur === '조사완료' ? '#fbbf24' : '#fff',
                      }}>조사완료</Button>
                  </Tooltip>
                );
              }}
            </Form.Item>
          </div>
          <Form.List name="surveys_yakho">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle">
                    <Col flex="64px">
                      <Form.Item {...rest} name={[name, 'round_no']} label={key === 0 ? '차수' : undefined} style={{ marginBottom: 6 }}>
                        <InputNumber min={1} max={10} style={{ width: 56 }} />
                      </Form.Item>
                    </Col>
                    <Col flex="160px">
                      <Form.Item {...rest} name={[name, 'survey_date']} label={key === 0 ? '약효조사일자' : undefined} rules={[{ required: true, message: '필수' }]} style={{ marginBottom: 6 }}>
                        <DatePicker format={DATE_FORMAT} style={{ width: 148 }} placeholder="날짜 선택" />
                      </Form.Item>
                    </Col>
                    <Col flex="1">
                      <Form.Item {...rest} name={[name, 'note']} label={key === 0 ? '비고' : undefined} style={{ marginBottom: 6 }}>
                        <Input placeholder="비고" />
                      </Form.Item>
                    </Col>
                    <Col flex="28px">
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}
                        style={{ marginTop: key === 0 ? 22 : 0, padding: '0 4px' }} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, survey_date: null, note: '' })}
                  icon={<PlusOutlined />} style={{ width: '100%', marginBottom: 4, height: 26, fontSize: 12 }}>약효조사 추가</Button>
              </>
            )}
          </Form.List>

          {/* ── 약해조사일정 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 10px', margin: '6px 0 8px',
            background: '#fff7ed', borderLeft: '3px solid #ea580c', borderRadius: '0 6px 6px 0',
          }}>
            <Space size={6}>
              <AuditOutlined style={{ color: '#ea580c', fontSize: 12 }} />
              <span style={{ fontWeight: 600, fontSize: 11, color: '#ea580c' }}>약해조사일정</span>
            </Space>
            <span style={{ fontSize: 10, color: '#f97316' }}>선택 입력</span>
          </div>
          <Form.List name="surveys_yakhe">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle">
                    <Col flex="64px">
                      <Form.Item {...rest} name={[name, 'round_no']} label={key === 0 ? '차수' : undefined} style={{ marginBottom: 6 }}>
                        <InputNumber min={1} max={10} style={{ width: 56 }} />
                      </Form.Item>
                    </Col>
                    <Col flex="160px">
                      <Form.Item {...rest} name={[name, 'survey_date']} label={key === 0 ? '약해조사일자' : undefined} style={{ marginBottom: 6 }}>
                        <DatePicker format={DATE_FORMAT} style={{ width: 148 }} placeholder="날짜 선택" />
                      </Form.Item>
                    </Col>
                    <Col flex="1">
                      <Form.Item {...rest} name={[name, 'note']} label={key === 0 ? '비고' : undefined} style={{ marginBottom: 6 }}>
                        <Input placeholder="비고" />
                      </Form.Item>
                    </Col>
                    <Col flex="28px">
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}
                        style={{ marginTop: key === 0 ? 22 : 0, padding: '0 4px' }} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, survey_date: null, note: '' })}
                  icon={<PlusOutlined />} style={{ width: '100%', marginBottom: 4, height: 26, fontSize: 12 }}>약해조사 추가</Button>
              </>
            )}
          </Form.List>

          {/* ── 관리현황 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', margin: '6px 0 8px',
            background: '#f0fdf4', borderLeft: '3px solid #059669', borderRadius: '0 6px 6px 0',
          }}>
            <FileDoneOutlined style={{ color: '#059669', fontSize: 12 }} />
            <span style={{ fontWeight: 600, fontSize: 11, color: '#059669' }}>관리현황</span>
          </div>

          {/* Row: 성적서발급일(5) | TIMS(5) | 검토기록지(14) */}
          <Row gutter={8}>
            <Col span={5}>
              <Form.Item name="report_date" label="성적서발급일" style={{ marginBottom: 8 }}>
                <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="tims_status" label="TIMS입력현황" style={{ marginBottom: 8 }}>
                <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="review_record" label="검토기록지" style={{ marginBottom: 8 }}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* 성적서사진유무 — 반복등록 스타일 박스 */}
          <Form.Item name="has_result_photo" hidden><Input /></Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.has_result_photo !== c.has_result_photo}>
            {() => {
              const val: boolean | null | undefined = form.getFieldValue('has_result_photo');
              const isTrue = val === true;
              const isFalse = val === false;
              const boxBg = isTrue ? '#f0fdf4' : isFalse ? '#fef2f2' : '#f8fafc';
              const boxBorder = isTrue ? '#bbf7d0' : isFalse ? '#fecaca' : '#e2e8f0';
              return (
                <Form.Item style={{ marginBottom: 0 }}>
                  <div style={{
                    background: boxBg, border: `1px solid ${boxBorder}`,
                    borderRadius: 8, padding: '7px 12px', display: 'flex',
                    alignItems: 'center', gap: 8, transition: 'all 0.2s',
                  }}>
                    <span style={{ fontSize: 13, color: gray[600], fontWeight: 500, flexShrink: 0 }}>성적서사진유무</span>
                    {(['있음', '없음'] as const).map(opt => {
                      const optVal = opt === '있음';
                      const selected = val === optVal;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => form.setFieldValue('has_result_photo', selected ? null : optVal)}
                          style={{
                            padding: '3px 12px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                            border: `1.5px solid ${selected ? (optVal ? '#22c55e' : '#ef4444') : '#e2e8f0'}`,
                            background: selected ? (optVal ? '#dcfce7' : '#fee2e2') : '#fafafa',
                            color: selected ? (optVal ? '#16a34a' : '#dc2626') : '#64748b',
                            fontWeight: selected ? 700 : 400, transition: 'all 0.12s', lineHeight: '20px',
                          }}
                        >{opt}</button>
                      );
                    })}
                    {(val !== null && val !== undefined) && (
                      <button
                        type="button"
                        onClick={() => form.setFieldValue('has_result_photo', null)}
                        style={{ fontSize: 11, color: gray[400], background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
                      >✕</button>
                    )}
                  </div>
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
      {/* 처리일정 모달 */}
      <Modal
        title={itemModalSchedule ? `처리일정 — ${itemModalSchedule.test_number} ${itemModalSchedule.title}` : '처리일정'}
        open={treatmentModalVisible}
        onCancel={() => setTreatmentModalVisible(false)}
        onOk={handleTreatmentSave}
        confirmLoading={itemModalSaving}
        width={680}
        okText="저장"
        cancelText="취소"
        maskClosable={false}
      >
        <Form form={treatmentForm} size="small">
          <Form.List name="treatments">
            {(fields, { add, remove }) => (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontWeight: 600, color: gray[500], fontSize: 12, padding: '0 4px' }}>
                  <span style={{ width: 60, flexShrink: 0 }}>차수</span>
                  <span style={{ width: 160, flexShrink: 0 }}>처리일자</span>
                  <span style={{ flex: 1 }}>비고</span>
                  <span style={{ width: 28, flexShrink: 0 }} />
                </div>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle" style={{ marginBottom: 4 }}>
                    <Col flex="60px">
                      <Form.Item {...rest} name={[name, 'round_no']} noStyle>
                        <InputNumber min={1} max={7} style={{ width: '100%' }} placeholder="차수" />
                      </Form.Item>
                    </Col>
                    <Col flex="160px">
                      <Form.Item {...rest} name={[name, 'treatment_date']} noStyle rules={[{ required: true, message: '처리일자를 입력하세요' }]}>
                        <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="처리일자 선택" />
                      </Form.Item>
                    </Col>
                    <Col flex="1">
                      <Form.Item {...rest} name={[name, 'note']} noStyle>
                        <Input placeholder="비고" />
                      </Form.Item>
                    </Col>
                    <Col flex="28px">
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ round_no: fields.length + 1, treatment_date: null, note: '' })}
                  icon={<PlusOutlined />}
                  style={{ width: '100%', marginTop: 8, borderColor: '#7c3aed', color: '#7c3aed' }}
                >
                  처리 추가
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* ── 진행 타임라인 모달 ── */}
      <Modal
        title={timelineRecord ? (
          <span>
            <HistoryOutlined style={{ color: '#8b5cf6', marginRight: 8 }} />
            진행 타임라인 — <span style={{ color: brand.primary, fontWeight: 700 }}>{timelineRecord.test_number}</span>
            <span style={{ fontWeight: 400, color: gray[500], marginLeft: 8, fontSize: 13 }}>{timelineRecord.title}</span>
          </span>
        ) : '진행 타임라인'}
        open={timelineVisible}
        onCancel={() => setTimelineVisible(false)}
        footer={<Button onClick={() => setTimelineVisible(false)}>닫기</Button>}
        width={700}
        maskClosable={false}
      >
        {timelineRecord && (() => {
          interface TimelineEvent {
            date: string; label: string; actor: 'sappt' | 'partner';
            icon: React.ReactNode; note?: string;
            color: string; bg: string; border: string;
          }
          // 유형별 색상 — 색상환 균등 배분으로 최대 대비
          const TYPE_COLOR = {
            start:   { color: '#4338ca', bg: '#eef2ff', border: '#818cf8' }, // 인디고  — 시험개시
            treat:   { color: '#0369a1', bg: '#e0f2fe', border: '#38bdf8' }, // 하늘파랑 — 처리
            yakho:   { color: '#15803d', bg: '#dcfce7', border: '#4ade80' }, // 초록    — 약효조사
            yakhe:   { color: '#c2410c', bg: '#ffedd5', border: '#fb923c' }, // 주황    — 약해조사
            done:    { color: '#0d9488', bg: '#ccfbf1', border: '#2dd4bf' }, // 청록    — 완료/TIMS
            partner: { color: '#b45309', bg: '#fef3c7', border: '#fbbf24' }, // 골드앰버 — 파트너확인
          };
          const events: TimelineEvent[] = [];
          if (timelineRecord.start_date)
            events.push({ date: timelineRecord.start_date, label: '시험 개시', actor: 'sappt', icon: <CalendarOutlined />, ...TYPE_COLOR.start });
          (timelineRecord.treatments || []).filter(t => t.treatment_date).forEach(t =>
            events.push({ date: t.treatment_date, label: `${t.round_no}차 처리`, actor: 'sappt', icon: <ExperimentOutlined />, note: t.note || undefined, ...TYPE_COLOR.treat })
          );
          (timelineRecord.surveys || []).filter(s => s.survey_date).forEach(s => {
            const isYakhe = s.survey_type === '약해';
            events.push({ date: s.survey_date, label: `${s.round_no}차 ${isYakhe ? '약해조사' : '약효조사'}`, actor: 'sappt', icon: <AuditOutlined />, note: s.note || undefined, ...(isYakhe ? TYPE_COLOR.yakhe : TYPE_COLOR.yakho) });
          });
          if (timelineRecord.report_date)
            events.push({ date: timelineRecord.report_date, label: '성적서 발급', actor: 'sappt', icon: <FileDoneOutlined />, ...TYPE_COLOR.done });
          if (timelineRecord.tims_status)
            events.push({ date: timelineRecord.tims_status, label: 'TIMS 입력', actor: 'sappt', icon: <CheckCircleOutlined />, ...TYPE_COLOR.done });
          if (timelineRecord.partner_confirmed_at)
            events.push({ date: timelineRecord.partner_confirmed_at.substring(0, 10), label: '파트너 확인', actor: 'partner', icon: <UserOutlined />, note: timelineRecord.partner_confirmed_by || undefined, ...TYPE_COLOR.partner });

          const isIso = (d: string) => /^\d{4}-\d{2}-\d{2}/.test(d);
          events.sort((a, b) => {
            if (!isIso(a.date) && !isIso(b.date)) return 0;
            if (!isIso(a.date)) return -1;
            if (!isIso(b.date)) return 1;
            return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
          });

          const sapptCnt = events.filter(e => e.actor === 'sappt').length;
          const partnerCnt = events.filter(e => e.actor === 'partner').length;

          return (
            <div>
              {/* 상단 요약 바 */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, padding: '8px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <Tag color={statusTagColors[timelineRecord.status] || '#64748b'} style={{ marginRight: 0, fontWeight: 600 }}>{timelineRecord.status}</Tag>
                  <span style={{ fontSize: 12, color: gray[500] }}><TeamOutlined style={{ marginRight: 4, color: '#4338ca' }} /><span style={{ color: '#4338ca', fontWeight: 600 }}>SAPPT</span> {sapptCnt}건</span>
                  <span style={{ fontSize: 12, color: gray[500] }}><UserOutlined style={{ marginRight: 4, color: '#b45309' }} /><span style={{ color: '#b45309', fontWeight: 600 }}>파트너</span> {partnerCnt}건</span>
                </div>
                {/* 색상 범례 */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '6px 10px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  {([
                    { color: '#4338ca', bg: '#eef2ff', label: '시험개시' },
                    { color: '#0369a1', bg: '#e0f2fe', label: '처리' },
                    { color: '#15803d', bg: '#dcfce7', label: '약효조사' },
                    { color: '#c2410c', bg: '#ffedd5', label: '약해조사' },
                    { color: '#0d9488', bg: '#ccfbf1', label: '완료/TIMS' },
                    { color: '#b45309', bg: '#fef3c7', label: '파트너확인' },
                  ] as const).map(l => (
                    <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, color: l.color, background: l.bg, border: `1px solid ${l.color}44`, borderRadius: 12, padding: '2px 8px' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: l.color, display: 'inline-block', flexShrink: 0 }} />{l.label}
                    </span>
                  ))}
                </div>
              </div>

              {events.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: gray[400] }}>등록된 일정 정보가 없습니다.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 86px 1fr', gap: 0 }}>
                  {/* 컬럼 헤더 */}
                  <div style={{ textAlign: 'right', paddingRight: 12, paddingBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#4338ca', letterSpacing: '0.04em' }}><TeamOutlined style={{ marginRight: 4 }} />SAPPT</span>
                  </div>
                  <div />
                  <div style={{ paddingLeft: 12, paddingBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309', letterSpacing: '0.04em' }}><UserOutlined style={{ marginRight: 4 }} />의뢰사</span>
                  </div>

                  {/* 이벤트 행 */}
                  {events.map((ev, idx) => (
                    <React.Fragment key={idx}>
                      {/* SAPPT 측 */}
                      <div style={{ textAlign: 'right', paddingRight: 12, paddingBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {ev.actor === 'sappt' && (
                          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', background: ev.bg, borderLeft: `3px solid ${ev.color}`, border: `1px solid ${ev.border}`, borderRadius: '7px 7px 7px 4px', padding: '5px 11px', maxWidth: 220 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: ev.color, fontWeight: 700, fontSize: 13 }}>
                              {ev.icon}<span>{ev.label}</span>
                            </div>
                            {ev.note && <span style={{ fontSize: 11, color: gray[400], marginTop: 2 }}>{ev.note}</span>}
                          </div>
                        )}
                      </div>

                      {/* 중앙 타임라인 */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                        {idx > 0 && <div style={{ width: 2, height: 14, background: '#cbd5e1', marginBottom: 2 }} />}
                        <div style={{ width: 14, height: 14, borderRadius: '50%', background: ev.color, border: '2.5px solid #fff', boxShadow: `0 0 0 2.5px ${ev.color}60`, flexShrink: 0, zIndex: 1 }} />
                        <div style={{ fontSize: 10, color: gray[500], fontWeight: 500, marginTop: 3, textAlign: 'center', whiteSpace: 'nowrap', lineHeight: 1.3 }}>{ev.date}</div>
                        {idx < events.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 10, background: '#cbd5e1', marginTop: 2 }} />}
                      </div>

                      {/* 파트너 측 */}
                      <div style={{ paddingLeft: 12, paddingBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        {ev.actor === 'partner' && (
                          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', background: ev.bg, borderRight: `3px solid ${ev.color}`, border: `1px solid ${ev.border}`, borderRadius: '7px 4px 7px 7px', padding: '5px 11px', maxWidth: 220 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: ev.color, fontWeight: 700, fontSize: 13 }}>
                              {ev.icon}<span>{ev.label}</span>
                            </div>
                            {ev.note && <span style={{ fontSize: 11, color: gray[400], marginTop: 2 }}>{ev.note}</span>}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* 미완료 항목 요약 */}
              {(() => {
                const missing: string[] = [];
                if (!timelineRecord.report_date) missing.push('성적서 발급');
                if (!timelineRecord.partner_confirmed_at) missing.push('파트너 확인');
                if (timelineRecord.status !== '최종종료') missing.push(`최종종료 (현재: ${timelineRecord.status})`);
                if (missing.length === 0) return null;
                return (
                  <div style={{ marginTop: 8, padding: '10px 14px', background: '#fefce8', borderRadius: 8, border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>⏳ 미완료 항목</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {missing.map(m => <Tag key={m} color="warning" style={{ fontSize: 11 }}>{m}</Tag>)}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </Modal>

      {/* 조사일정 모달 — type별 단독 표시 */}
      <Modal
        title={
          itemModalSchedule ? (
            <Space size={6}>
              <AuditOutlined style={{ color: surveyModalType === '약효' ? '#0891b2' : '#ea580c' }} />
              <span style={{ color: surveyModalType === '약효' ? '#0891b2' : '#ea580c', fontWeight: 700 }}>
                {surveyModalType === '약효' ? '약효' : '약해'}조사일정
              </span>
              <span style={{ color: gray[500], fontWeight: 400, fontSize: 13 }}>
                — {itemModalSchedule.test_number}
              </span>
            </Space>
          ) : '조사일정'
        }
        open={surveyModalVisible}
        onCancel={() => setSurveyModalVisible(false)}
        onOk={handleSurveySave}
        confirmLoading={itemModalSaving}
        width={640}
        okText="저장"
        cancelText="취소"
        maskClosable={false}
        styles={{ header: {
          borderBottom: `2px solid ${surveyModalType === '약효' ? '#0891b2' : '#ea580c'}`,
          paddingBottom: 12,
        }}}
      >
        <Form form={surveyForm} size="small">
          {surveyModalType === '약효' ? (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 10px', margin: '8px 0 10px',
                background: '#ecfeff', borderLeft: '3px solid #0891b2', borderRadius: '0 6px 6px 0',
              }}>
                <AuditOutlined style={{ color: '#0891b2', fontSize: 12 }} />
                <span style={{ fontWeight: 600, fontSize: 11, color: '#0891b2' }}>약효조사일정 등록/수정</span>
              </div>
              <Form.List name="surveys_yakho">
                {(fields, { add, remove }) => (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontWeight: 600, color: gray[500], fontSize: 12, padding: '0 4px' }}>
                      <span style={{ width: 60, flexShrink: 0 }}>차수</span>
                      <span style={{ width: 160, flexShrink: 0 }}>약효조사일자</span>
                      <span style={{ flex: 1 }}>비고</span>
                      <span style={{ width: 28 }} />
                    </div>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row gutter={8} key={key} align="middle" style={{ marginBottom: 4 }}>
                        <Col flex="60px"><Form.Item {...rest} name={[name, 'round_no']} noStyle><InputNumber min={1} max={10} style={{ width: '100%' }} /></Form.Item></Col>
                        <Col flex="160px"><Form.Item {...rest} name={[name, 'survey_date']} noStyle rules={[{ required: true, message: '필수' }]}><DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택" /></Form.Item></Col>
                        <Col flex="1"><Form.Item {...rest} name={[name, 'note']} noStyle><Input placeholder="비고" /></Form.Item></Col>
                        <Col flex="28px"><Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} /></Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, survey_date: null, note: '' })}
                      icon={<PlusOutlined />} style={{ width: '100%', marginTop: 4, borderColor: '#0891b2', color: '#0891b2' }}>약효조사 추가</Button>
                  </>
                )}
              </Form.List>
            </>
          ) : (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 10px', margin: '8px 0 10px',
                background: '#fff7ed', borderLeft: '3px solid #ea580c', borderRadius: '0 6px 6px 0',
              }}>
                <AuditOutlined style={{ color: '#ea580c', fontSize: 12 }} />
                <span style={{ fontWeight: 600, fontSize: 11, color: '#ea580c' }}>약해조사일정 등록/수정</span>
              </div>
              <Form.List name="surveys_yakhe">
                {(fields, { add, remove }) => (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontWeight: 600, color: gray[500], fontSize: 12, padding: '0 4px' }}>
                      <span style={{ width: 60, flexShrink: 0 }}>차수</span>
                      <span style={{ width: 160, flexShrink: 0 }}>약해조사일자</span>
                      <span style={{ flex: 1 }}>비고</span>
                      <span style={{ width: 28 }} />
                    </div>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row gutter={8} key={key} align="middle" style={{ marginBottom: 4 }}>
                        <Col flex="60px"><Form.Item {...rest} name={[name, 'round_no']} noStyle><InputNumber min={1} max={10} style={{ width: '100%' }} /></Form.Item></Col>
                        <Col flex="160px"><Form.Item {...rest} name={[name, 'survey_date']} noStyle><DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택" /></Form.Item></Col>
                        <Col flex="1"><Form.Item {...rest} name={[name, 'note']} noStyle><Input placeholder="비고" /></Form.Item></Col>
                        <Col flex="28px"><Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} /></Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, survey_date: null, note: '' })}
                      icon={<PlusOutlined />} style={{ width: '100%', marginTop: 4, borderColor: '#ea580c', color: '#ea580c' }}>약해조사 추가</Button>
                  </>
                )}
              </Form.List>
            </>
          )}
        </Form>
      </Modal>

      {/* ── 출장 등록 모달 ── */}
      <Modal
        title={<Space size={6}><CarOutlined style={{ color: brand.primary }} /><span style={{ fontWeight: 700 }}>업무 및 출장 등록</span></Space>}
        open={tripModalVisible}
        onCancel={() => setTripModalVisible(false)}
        onOk={handleTripSave}
        confirmLoading={tripSaving}
        okText="등록"
        cancelText="취소"
        width={580}
        okButtonProps={{ style: { background: brand.primary, borderColor: brand.primary } }}
        styles={{ body: { padding: '16px 20px 4px' } }}
      >
        <Form form={tripForm} layout="vertical" size="middle" initialValues={{ work_type: '외근' }}>

          {/* 구분 */}
          <Form.Item label="구분" name="work_type" style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {['out', 'in', 'leave'].map((group, gi) => (
                <React.Fragment key={group}>
                  {TRIP_WORK_TYPES.filter(t => t.group === group).map(t => {
                    const colors = TRIP_TYPE_COLOR[group];
                    const selected = (tripWatchedWorkType || '외근') === t.value;
                    return (
                      <button key={t.value} type="button"
                        onClick={() => tripForm.setFieldValue('work_type', t.value)}
                        style={{
                          padding: '3px 9px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                          border: `1.5px solid ${selected ? colors.border : '#e2e8f0'}`,
                          background: selected ? colors.bg : '#fafafa',
                          color: selected ? colors.text : '#64748b',
                          fontWeight: selected ? 700 : 400, lineHeight: '20px',
                        }}>
                        {t.icon} {t.label}
                      </button>
                    );
                  })}
                  {gi < 2 && <div style={{ width: 1, background: '#e2e8f0', margin: '0 3px' }} />}
                </React.Fragment>
              ))}
            </div>
          </Form.Item>

          {/* 일정 + 기간 */}
          <Row gutter={8}>
            <Col span={16}>
              <Form.Item label="일정" name="date_range" rules={[{ required: true, message: '날짜를 선택하세요.' }]} style={{ marginBottom: 10 }}>
                <RangePicker style={{ width: '100%' }} placeholder={['시작일', '종료일']} allowEmpty={[false, true]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="기간" name="duration" style={{ marginBottom: 10 }}>
                <Select placeholder="기간" allowClear>
                  <Option value="종일">종일</Option>
                  <Option value="오전">오전</Option>
                  <Option value="오후">오후</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 연구원 + 차량 */}
          <Row gutter={8}>
            <Col span={isTripOutdoor(tripWatchedWorkType) ? 14 : 24}>
              <Form.Item label="연구원" name="researchers" rules={[{ required: true, message: '연구원을 입력하세요.' }]} style={{ marginBottom: 10 }}>
                <Select mode="tags" placeholder="연구원 선택 또는 입력"
                  options={tripResearchers.map(r => ({ value: r, label: r }))}
                  tokenSeparators={[',']} />
              </Form.Item>
            </Col>
            {isTripOutdoor(tripWatchedWorkType) && (
              <Col span={10}>
                <Form.Item label="차량" name="vehicle_number" style={{ marginBottom: 10 }}>
                  <Select mode="tags" placeholder="차량 선택"
                    options={tripVehicles.map(v => ({ value: v, label: v }))}
                    tokenSeparators={[',']} />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* 작업내용 */}
          <Form.Item label="작업내용" name="work_content" style={{ marginBottom: 10 }}>
            <Input.TextArea placeholder="작업 내용 또는 사유" autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>

          {/* 외근 전용 */}
          {isTripOutdoor(tripWatchedWorkType) && (
            <Row gutter={8}>
              <Col span={14}>
                <Form.Item label="출장지" name="destination" style={{ marginBottom: 10 }}>
                  <Input prefix={<EnvironmentOutlined style={{ color: gray[400] }} />} placeholder="예: 서산, 보령" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item label="시험번호" name="test_number" style={{ marginBottom: 10 }}>
                  <Input readOnly style={{ background: '#f8fafc', color: brand.primary, fontWeight: 600 }} />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* 시험책임자 확인일 + 비고 */}
          <Row gutter={8}>
            <Col span={10}>
              <Form.Item label="시험책임자 확인일" name="supervisor_confirmed_at" style={{ marginBottom: 4 }}>
                <DatePicker style={{ width: '100%' }} placeholder="확인일" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item label="비고" name="note" style={{ marginBottom: 4 }}>
                <Input.TextArea placeholder="기타 메모" autoSize={{ minRows: 1, maxRows: 3 }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SchedulePage;
